import React, { useState } from 'react';
import { aiApi } from '../../utils/api';

interface ReprocessModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    _id: string;
    title: string;
    description?: string;
    images: string[];
    condition: 'new' | 'used' | 'salvage';
    materials: string[];
    dimensions?: {
      width: number;
      height: number;
      depth: number;
      units: string;
    };
  };
  onReprocessComplete: (updatedAnalysis: any) => void;
}

export const ReprocessModal: React.FC<ReprocessModalProps> = ({
  isOpen,
  onClose,
  item,
  onReprocessComplete
}) => {
  const [contextInfo, setContextInfo] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contextInfo.trim()) {
      setError('Please provide some context about the item');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      // Call the reprocess API with additional context
      const response = await aiApi.reprocessItem({
        itemId: item._id,
        additionalContext: contextInfo.trim(),
        imageUrl: item.images[0], // Use the first image
        currentTitle: item.title,
        currentDescription: item.description,
        condition: item.condition,
        materials: item.materials,
        dimensions: item.dimensions
      });

      if (response.data.success && response.data.data) {
        onReprocessComplete(response.data.data);
        onClose();
        setContextInfo('');
      } else {
        throw new Error('Failed to reprocess item');
      }
    } catch (error: any) {
      console.error('Reprocessing failed:', error);
      setError(error.response?.data?.message || 'Failed to reprocess item. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
      setContextInfo('');
      setError('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Reprocess Item with AI
          </h2>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <p className="mb-4">
              Provide additional context about <strong>"{item.title}"</strong> to help the AI better understand and analyze this item.
            </p>
            <p>
              For example, you could mention specific features, historical significance, condition details, 
              or any unique characteristics that would help improve the AI's estimation and description.
            </p>
          </div>

          {/* Context Input */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Additional Context *
            </label>
            <textarea
              value={contextInfo}
              onChange={(e) => setContextInfo(e.target.value)}
              placeholder="e.g., This is a vintage 1950s oak dining table with original hardware. It has some minor scratches on the surface but the structure is solid. The table was handcrafted by a local artisan and has sentimental value..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg 
                       bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                       focus:ring-2 focus:ring-primary-500 focus:border-transparent
                       min-h-[120px] resize-y"
              disabled={isProcessing}
              required
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Be as detailed as possible. The more context you provide, the better the AI can understand your item.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Current Item Info */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Current Item Info:</h4>
            <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <p><strong>Title:</strong> {item.title}</p>
              {item.description && <p><strong>Description:</strong> {item.description}</p>}
              <p><strong>Condition:</strong> {item.condition}</p>
              {item.materials.length > 0 && (
                <p><strong>Materials:</strong> {item.materials.join(', ')}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleClose}
              disabled={isProcessing}
              className="btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !contextInfo.trim()}
              className={`
                px-6 py-2 rounded-lg font-medium transition-colors
                ${isProcessing || !contextInfo.trim()
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                }
              `}
            >
              {isProcessing ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Reprocessing...</span>
                </div>
              ) : (
                'Reprocess with AI'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
